import debug from "debug";
import * as THREE from "three";
const log = debug("app:marquee");

/**
 * Class for handling marquee selection in a 3D scene, allowing users to select objects with a rectangular selection tool.
 * @category Experience
 * @subcategory ObjectManagement
 */
class MarqueeSelection {
  /**
   * Constructs the MarqueeSelection instance.
   * @param {Object} experience - The main experience object.
   * @param {THREE.Scene} experience.scene - The 3D scene containing objects.
   * @param {Object} experience.sizes - The sizes of the rendering area.
   * @param {THREE.Camera} experience.camera - The main camera instance.
   * @param {THREE.WebGLRenderer} experience.renderer - The WebGL renderer instance.
   * @param {Object} experience.eventHub - The event hub for handling events.
   */
  constructor(experience) {
    this.experience = experience;
    this.scene = experience.scene;
    this.sizes = experience.sizes;
    this.camera = experience.camera.instance;
    this.renderer = experience.renderer.instance;
    this.eventHub = experience.eventHub;
    this.raycaster = new THREE.Raycaster();
    this.isSelecting = false;
    this.is3DModeActive = false; // Initialize the 3D mode flag
    this.basePlane = this.experience.world.basePlane.mesh;

    // Setup immediately upon instantiation

    this.setupCoreListeners();
  }



  /**
   * Sets up the core event listeners for handling marquee actions and 3D mode toggling.
   */
  setupCoreListeners() {
    this.eventHub.on("marqueeStarted", this.handleMarqueeStarted.bind(this));
    this.eventHub.on(
      "modifierMarqueeStarted",
      this.handleMarqueeStarted.bind(this)
    );
    this.eventHub.on("3d-enabled", (event) => {
      this.is3DModeActive = event.enabled;
      log(`3D mode ${event.enabled ? "activated" : "deactivated"}`);
    });
  }

  /**
   * Handles the start of a marquee selection.
   * @param {Event} event - The mouse or touch event that starts the marquee selection.
   */
  handleMarqueeStarted(event) {
    log("handleMarquee started");

    if (event.button !== 0 && event.button !== 1 && !event.touches) {
      log("event.button !== 0 && !event.touches", event.button, event.touches);
      return;
    }
    if (this.is3DModeActive) {
      log("✅ 3D Mode active, allowing marquee selection.");
  }

    this.cleanup();

    this.startMarqueeListeners();
    this.setupRectangle();
    this.startPoint = new THREE.Vector2(
      event.startX || event.clientX,
      event.startY || event.clientY
    );
    this.endPoint = new THREE.Vector2();
    log("starting point", this.startPoint);
    this.isSelecting = true;
    //this.updateRectangle();
    log("Marquee selection started at", this.startPoint);
  }

  updateBasePlane() {
    if (!this.basePlane) {
        log("⚠️ BasePlane is missing! 3D selection may fail.");
        return;
    }

    const cameraDirection = new THREE.Vector3();
    this.camera.getWorldDirection(cameraDirection);

    this.basePlane.position.copy(this.camera.position).add(cameraDirection.multiplyScalar(10));
    this.basePlane.lookAt(this.camera.position);

    log("🔄 Updated basePlane position and orientation.");
}


  /**
   * Starts listening for marquee-specific events like mouse or touch movements.
   */
  startMarqueeListeners() {
    this.boundHandleMouseMove = this.handleMouseMove.bind(this);
    this.boundHandleMouseUp = this.handleMouseUp.bind(this);
    this.boundHandleTouchMove = this.handleTouchMove.bind(this);
    this.boundHandleTouchEnd = this.handleTouchEnd.bind(this);

    this.eventHub.on("globalMouseMove", this.boundHandleMouseMove);
    this.eventHub.on("globalMouseUp", this.boundHandleMouseUp);
    this.eventHub.on("globalTouchMove", this.boundHandleTouchMove);
    this.eventHub.on("globalTouchEnd", this.boundHandleTouchEnd);
  }

  /**
   * Stops listening for marquee-specific events.
   */
  stopMarqueeListeners() {
    this.eventHub.off("globalMouseMove", this.boundHandleMouseMove);
    this.eventHub.off("globalMouseUp", this.boundHandleMouseUp);
    this.eventHub.off("globalTouchMove", this.boundHandleTouchMove);
    this.eventHub.off("globalTouchEnd", this.boundHandleTouchEnd);
  }

   /**
   * Handles mouse movement during marquee selection.
   * @param {MouseEvent} event - The mouse move event.
   */
  handleMouseMove(event) {
    if (!this.isSelecting || !event) return;
    this.endPoint = new THREE.Vector2(event.clientX || 0, event.clientY || 0);
    this.updateRectangle();
  }

  /**
   * Handles mouse release to finalize the marquee selection.
   * @param {MouseEvent} event - The mouse up event.
   */
  handleMouseUp(event) {
    if (event.button !== 0 && !event.touches) return;

    this.finalizeSelection(event);
    this.cleanup(); // Cleanup and dispose
  }

  /**
   * Handles touch movement during marquee selection.
   * @param {TouchEvent} event - The touch move event.
   */
  handleTouchMove(event) {
    if (!this.isSelecting) return;
    const touch = event.touches[0];
    this.endPoint = new THREE.Vector2(touch.clientX, touch.clientY);
    this.updateRectangle();
  }

  /**
   * Handles touch release to finalize the marquee selection.
   * @param {TouchEvent} event - The touch end event.
   */

  handleTouchEnd(event) {
    this.finalizeSelection(event);
    this.cleanup(); // Cleanup and dispose
  }

  /**
   * Updates the dimensions and position of the selection rectangle.
   */
  updateRectangle() {
    this.rect.style.display = "block";
    const x = Math.min(this.startPoint.x, this.endPoint.x);
    const y = Math.min(this.startPoint.y, this.endPoint.y);
    const width = Math.abs(this.endPoint.x - this.startPoint.x);
    const height = Math.abs(this.endPoint.y - this.startPoint.y);
    this.rect.style.left = `${x}px`;
    this.rect.style.top = `${y}px`;
    this.rect.style.width = `${width}px`;
    this.rect.style.height = `${height}px`;
  }

  /**
   * Finalizes the selection by creating a selection box and determining the selected objects.
   * @param {Event} event - The event triggering the finalization.
   */
  finalizeSelection(event) {
    const start = this.convertScreenToWorld(this.startPoint);
    const end = this.convertScreenToWorld(this.endPoint);

    let selectionVolume;

    if (!this.is3DModeActive) {
        // ✅ Keep the fixed 2D logic unchanged!
        const planarView = this.getPlanarView() || "xy";
        let minPoint, maxPoint;

        switch (planarView) {
            case "xy":
                minPoint = new THREE.Vector3(Math.min(start.x, end.x), Math.min(start.y, end.y), -Infinity);
                maxPoint = new THREE.Vector3(Math.max(start.x, end.x), Math.max(start.y, end.y), Infinity);
                break;
            case "xz":
                minPoint = new THREE.Vector3(Math.min(start.x, end.x), -Infinity, Math.min(start.z, end.z));
                maxPoint = new THREE.Vector3(Math.max(start.x, end.x), Infinity, Math.max(start.z, end.z));
                break;
            case "yz":
                minPoint = new THREE.Vector3(-Infinity, Math.min(start.y, end.y), Math.min(start.z, end.z));
                maxPoint = new THREE.Vector3(Infinity, Math.max(start.y, end.y), Math.max(start.z, end.z));
                break;
        }

        selectionVolume = new THREE.Box3(minPoint, maxPoint);
        log(`📦 2D Selection Box on ${planarView} plane`, selectionVolume);
    } else {
        // ✅ Fixing 3D Selection From Below
        const raycaster = new THREE.Raycaster();
        const groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);

        function projectOntoPlane(screenPoint) {
            raycaster.setFromCamera(screenPoint, this.camera);
            const projectedPoint = new THREE.Vector3();
            raycaster.ray.intersectPlane(groundPlane, projectedPoint);
            return projectedPoint;
        }

        const ndcStart = new THREE.Vector2(
            (this.startPoint.x / this.sizes.reSize.width) * 2 - 1,
            -(this.startPoint.y / this.sizes.reSize.height) * 2 + 1
        );

        const ndcEnd = new THREE.Vector2(
            (this.endPoint.x / this.sizes.reSize.width) * 2 - 1,
            -(this.endPoint.y / this.sizes.reSize.height) * 2 + 1
        );

        const worldStart = projectOntoPlane.call(this, ndcStart);
        const worldEnd = projectOntoPlane.call(this, ndcEnd);

        // ✅ Fixing Bounding Box Issues for Views From Below
        let minX = Math.min(worldStart.x, worldEnd.x);
        let maxX = Math.max(worldStart.x, worldEnd.x);
        let minY = Math.min(worldStart.y, worldEnd.y);
        let maxY = Math.max(worldStart.y, worldEnd.y);
        let minZ = Math.min(worldStart.z, worldEnd.z);
        let maxZ = Math.max(worldStart.z, worldEnd.z);

        // 📌 Ensure full bounding volume regardless of camera angle
        selectionVolume = new THREE.Box3(
            new THREE.Vector3(minX, Math.min(minY, maxY) - 5, minZ),
            new THREE.Vector3(maxX, Math.max(minY, maxY) + 5, maxZ)
        );

        log("📦 3D Selection Volume (Fixed for Below Views):", selectionVolume);
    }

    // 🔹 Check which objects are inside the selection box
    const selectedObjects = this.scene.children.filter((child) => {
        if (child.name === "XMO") {
            const box = new THREE.Box3();
            if (child.geometry) {
                child.geometry.computeBoundingBox();
                box.copy(child.geometry.boundingBox).applyMatrix4(child.matrixWorld);
            } else {
                box.setFromObject(child);
            }

            return selectionVolume.intersectsBox(box);
        }
        return false;
    });

    this.eventHub.emit("multiSelect", selectedObjects);
    log("🔴 Selected Objects:", selectedObjects);
}



  /**
   * Sets up the rectangle element used for the marquee selection.
   */
  setupRectangle() {
    this.rect = document.createElement("div");
    this.rect.style.border = "1px dashed black";
    this.rect.style.position = "absolute";
    this.rect.style.pointerEvents = "none";
    this.rect.style.display = "none";
    document.body.appendChild(this.rect);
    log("Rectangle setup completed");
  }

  /**
   * Determines the planar view (XY, XZ, YZ) based on the camera's up vector.
   * @returns {string|null} The planar view (e.g., "xy", "xz", "yz") or null if in 3D view.
   */
  getPlanarView() {
    const up = this.camera.up;
    if (Math.abs(up.dot(new THREE.Vector3(0, 1, 0))) === 1) return "xz";
    if (Math.abs(up.dot(new THREE.Vector3(0, 0, 1))) === 1) return "xy";
    if (Math.abs(up.dot(new THREE.Vector3(1, 0, 0))) === 1) return "yz";
    return null;
  }

  /**
   * Converts screen coordinates to world coordinates using raycasting.
   * @param {THREE.Vector2} vector - The screen coordinates to convert.
   * @returns {THREE.Vector3} The converted world coordinates.
   */
  convertScreenToWorld(vector) {
    const x = (vector.x / this.sizes.reSize.width) * 2 - 1;
    const y = -(vector.y / this.sizes.reSize.height) * 2 + 1;
    const mouse = new THREE.Vector2(x, y);

    log("📏 Converted screen coordinates:", mouse);

    this.raycaster.setFromCamera(mouse, this.camera);

    if (!this.basePlane) {
        log("❌ Base plane is missing! Returning default vector.");
        return new THREE.Vector3(0, 0, 0);
    }

    const intersects = this.raycaster.intersectObjects(this.scene.children, true);

if (!intersects.length) {
  log("❌ No intersections at all.");
  return new THREE.Vector3(0, 0, 0);
}

let basePlaneHit = null;

for (let i = 0; i < intersects.length; i++) {
  const obj = intersects[i].object;
  if (obj === this.basePlane) {
    basePlaneHit = intersects[i];
    continue;
  }

  log("✅ Found non-basePlane intersection at:", intersects[i].point);
  return intersects[i].point;
}

log("🟨 Only basePlane was hit. Returning basePlane hit:", basePlaneHit?.point);
return basePlaneHit?.point || new THREE.Vector3(0, 0, 0);

}




  /**
   * Cleans up the marquee selection instance by stopping listeners and removing the rectangle element.
   */
  cleanup() {
    this.stopMarqueeListeners(); // Stop listening to events
    if (this.rect && this.rect.parentElement) {
      document.body.removeChild(this.rect); // Remove rectangle from DOM
    }
    log("MarqueeSelection instance cleaned up and disposed");
  }
}

export default MarqueeSelection;
